import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Header,
  Textarea,
  Popover,
  StatusIndicator,
} from "@amzn/awsui-components-react";
import * as tokens from "@amzn/awsui-design-tokens-core";
import { Investigation, Stage } from "../../lusca-search-api/generated-src";
import {
  InvestigationApiError,
  useUpdateInvestigation,
} from "../../hooks/useInvestigations";
import { saveErrorText } from "./versionConflict";

// A wrapping Textarea (not Input) so a long title wraps across lines while editing, like the h1,
// instead of scrolling sideways in one line. field-sizing grows it to fit its content.
// Cloudscape Textarea has no size prop, and its own rules use an id-level :not(#\9) selector,
// so the override needs !important. Scoped by class to this editor only.
const TITLE_INPUT_CLASS = "lusca-investigation-title-input";
const TITLE_INPUT_CSS = `
.${TITLE_INPUT_CLASS} textarea {
  font-family: ${tokens.fontFamilyHeading} !important;
  font-size: ${tokens.fontSizeHeadingXl} !important;
  font-weight: ${tokens.fontWeightHeadingXl} !important;
  line-height: ${tokens.lineHeightHeadingXl} !important;
  padding-block: 2px !important;
  field-sizing: content;
  min-height: 0 !important;
  resize: none !important;
}`;

/** Mirrors MAX_TITLE_LENGTH on the server. */
export const MAX_TITLE_LENGTH = 200;

interface InvestigationTitleProps {
  stage: Stage;
  investigation: Investigation;
  currentUser: string | null;
  onError: (message: string) => void;
  /** Badges shown under the title (Header description); kept visible while editing. */
  description?: React.ReactNode;
}

/**
 * The page title, renamable in place by anyone signed in — not just the owner. Titles are guessed
 * when the case is opened (usually "error type on queue"); whoever understands the cause first can
 * give it a name that says what it actually is. Renames are audited as TITLE_CHANGED.
 */
export const InvestigationTitle: React.FC<InvestigationTitleProps> = ({
  stage,
  investigation,
  currentUser,
  onError,
  description,
}) => {
  const update = useUpdateInvestigation(stage);
  const canRename = !!currentUser;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(investigation.title);

  // A rename from elsewhere (another tab, MCP) while the form is closed should show through.
  useEffect(() => {
    if (!editing) setDraft(investigation.title);
  }, [investigation.title, editing]);

  const trimmed = draft.trim();
  const tooLong = trimmed.length > MAX_TITLE_LENGTH;
  const canSave = !!trimmed && !tooLong && !update.isLoading;

  const cancel = () => {
    setDraft(investigation.title);
    setEditing(false);
  };

  const save = () => {
    if (!canSave) return;
    if (trimmed === investigation.title) {
      setEditing(false);
      return;
    }
    update
      .mutateAsync({
        investigationId: investigation.id,
        request: { expectedVersion: investigation.version, title: trimmed },
      })
      .then(() => setEditing(false))
      .catch((err: InvestigationApiError) => onError(saveErrorText(err)));
  };

  if (!editing) {
    // Keep the pencil glued to the last word: a break opportunity sits between text and an
    // inline-block, so a nowrap span around only the button still lets it drop to its own line.
    const { title } = investigation;
    const cut = title.lastIndexOf(" ") + 1;
    return (
      <Header variant="h1" description={description}>
        {canRename ? (
          <>
            {title.slice(0, cut)}
            <span style={{ whiteSpace: "nowrap" }}>
              {title.slice(cut)}
              <span style={{ marginInlineStart: "6px" }}>
                <Button
                  variant="inline-icon"
                  iconName="edit"
                  ariaLabel="Rename investigation"
                  onClick={() => setEditing(true)}
                />
              </span>
            </span>
          </>
        ) : (
          title
        )}
      </Header>
    );
  }

  // Rendered outside Header: its h1 shrink-wraps its content, which squeezed the input to its
  // min-width. Here the editor takes the full title column.
  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: "8px",
          width: "100%",
          minHeight: "36px",
        }}
      >
        <style>{TITLE_INPUT_CSS}</style>
        <div className={TITLE_INPUT_CLASS} style={{ flex: 1, minWidth: 0 }}>
          <Textarea
            rows={1}
            value={draft}
            // Titles are one line: pasted newlines become spaces.
            onChange={({ detail }) =>
              setDraft(detail.value.replace(/\s*\n\s*/g, " "))
            }
            onKeyDown={(event) => {
              if (event.detail.key === "Enter") {
                event.preventDefault();
                save();
              }
              if (event.detail.key === "Escape") cancel();
            }}
            ariaLabel="Investigation title"
            placeholder="What is this investigation about?"
            autoFocus
            disabled={update.isLoading}
            invalid={tooLong}
          />
        </div>
        {tooLong && (
          <Popover
            content={`Titles are limited to ${MAX_TITLE_LENGTH} characters (${trimmed.length} now).`}
            dismissButton={false}
            size="small"
          >
            <StatusIndicator type="error">Too long</StatusIndicator>
          </Popover>
        )}
        <Button
          variant="primary"
          onClick={save}
          disabled={!canSave}
          loading={update.isLoading}
        >
          Save
        </Button>
        <Button onClick={cancel} disabled={update.isLoading}>
          Cancel
        </Button>
      </div>
      {description && <Box margin={{ top: "xxs" }}>{description}</Box>}
    </div>
  );
};

export default InvestigationTitle;
